/**
 * 移动端横向滑动返回 (左滑 / 右滑都能回上一级).
 *
 * 背景: 页面内的返回按钮正在统一去掉, iOS / Android 自带边缘侧滑返回, 但原生手势
 * 只认单侧边缘, 且部分浏览器 (Android 分屏 / 桌面触屏) 没有, 所以这里补一层页面内
 * 横滑返回, 与原生手势互补, 让各页面交互一致。
 *
 * 为避免抢事件, 以下情况不接管:
 * - 起手在屏幕左右边缘 24px 内: 留给系统原生边缘手势, 避免一次滑动触发两次返回;
 * - 起手落在视频、表单控件或可横向滚动的容器 (tab 条 / 筛选条) 上;
 * - 有弹层 (role="dialog") 或有视频播放时, 那属于它们自己的交互;
 * - 纵向位移明显大于横向时 (那是页面滚动, 不是返回)。
 */

/** 左右各留出的边缘宽度, 交给系统原生手势 */
const EDGE_GUARD_PX = 24
/** 触发返回的最小横向位移 */
const MIN_DISTANCE_PX = 60
/** 横向位移至少是纵向位移的多少倍才算「横滑」 */
const HORIZONTAL_RATIO = 1.5
/** 这些元素上的横向拖动是它们自己的交互 (拖动进度条等), 不算返回手势 */
const INTERACTIVE_SELECTOR = 'video, audio, input, textarea, select, [role="slider"], [role="dialog"]'

function hasVisibleElement(selector: string): boolean {
  return [...document.querySelectorAll(selector)].some((el) => el.getClientRects().length > 0)
}

/** 起手点是否在可横向滚动的容器内 (tab 条 / 筛选条), 是的话交给它自己滚动 */
function inHorizontalScroller(target: Element): boolean {
  let node: Element | null = target
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node)
    if (
      (style.overflowX === 'auto' || style.overflowX === 'scroll') &&
      node.scrollWidth > node.clientWidth + 4
    ) {
      return true
    }
    node = node.parentElement
  }
  return false
}

/** 绑定横滑返回; onBack 由调用方传入 (通常是 router.back) */
export function initSwipeBack(onBack: () => void): void {
  let startX = 0
  let startY = 0
  let tracking = false

  const stopTracking = (): void => {
    tracking = false
  }

  document.addEventListener(
    'touchstart',
    (event) => {
      stopTracking()
      if (event.touches.length !== 1) return
      const touch = event.touches[0]
      const target = event.target
      if (!touch || !(target instanceof Element)) return
      if (touch.clientX <= EDGE_GUARD_PX || touch.clientX >= window.innerWidth - EDGE_GUARD_PX) return
      if (target.closest(INTERACTIVE_SELECTOR) || inHorizontalScroller(target)) return
      if (hasVisibleElement('[role="dialog"]') || hasVisibleElement('video')) return
      tracking = true
      startX = touch.clientX
      startY = touch.clientY
    },
    { passive: true },
  )

  document.addEventListener('touchcancel', stopTracking, { passive: true })

  document.addEventListener(
    'touchend',
    (event) => {
      if (!tracking) return
      stopTracking()
      const touch = event.changedTouches[0]
      if (!touch) return
      const dx = Math.abs(touch.clientX - startX)
      const dy = Math.abs(touch.clientY - startY)
      if (dx < MIN_DISTANCE_PX || dx < dy * HORIZONTAL_RATIO) return
      onBack()
    },
    { passive: true },
  )
}
